import React from 'react';
import { Link as GatsbyLink, withPrefix } from 'gatsby';
import * as styles from './LinkButton.module.css';
import Image from './Image';

/**
 * LinkButtonコンポーネントに渡すPropsの型定義です。
 * @property children ボタン内に表示されるテキスト
 * @property to 遷移先のパスまたはURL
 */
export interface LinkButtonProps {
  /**
   * ボタン内に表示されるテキストコンテンツです。
   */
  children: React.ReactNode;
  /**
   * リンク先のパスまたはURLです。
   * 内部リンク（例: `/about`）の場合はGatsbyの`<Link>`を、
   * 外部リンク（例: `https://...` or `mailto:...`）の場合は通常の`<a>`タグを使用します。
   */
  to: string;

  /**
   * ボタンを中央揃えにするかどうか
   */
  center?: boolean;

  /**
   * ボタンに追加するCSSクラス名です。
   */
  className?: string;

  /**
   * ボタンに追加するスタイルです。
   */
  style?: React.CSSProperties;

  /**
   * テキストと矢印のgap
   */
  gap?: string;
}

/**
 * プロジェクト共通のリンクボタンコンポーネント。
 * 黄色背景・中央テキスト＋右端矢印アイコン。
 * 内部リンク・外部リンクを自動判別。
 *
 * @param {LinkButtonProps} props - このコンポーネントが受け取るprops。
 * @returns {JSX.Element} レンダリングされたLinkButtonコンポーネント。
 */
const LinkButton: React.FC<LinkButtonProps> = ({
  children,
  to,
  center,
  className,
  style,
  gap,
}) => {
  const isExternal =
    to.startsWith('mailto:') || /^(http|https)/.test(to) || to.endsWith('.pdf');
  const mergeClassName = className
    ? `${styles.button} ${className}`
    : styles.button;
  const buttonClassName = center
    ? `${mergeClassName} ${styles.buttonCenter}`
    : mergeClassName;

  const content = (
    <span className={styles.buttonContent} style={{ gap: gap }}>
      {children}
      <span className={styles.arrow} aria-hidden='true'>
        <Image src='/svg/arrow_right.svg' alt='' className={styles.arrowIcon} />
      </span>
    </span>
  );

  if (isExternal) {
    return (
      <a
        href={to}
        className={buttonClassName}
        target='_blank'
        rel='noopener noreferrer'
        style={style}
      >
        {content}
        <span className={styles.visuallyHidden}>（新しいタブで開きます）</span>
      </a>
    );
  }

  return (
    <GatsbyLink to={to} className={buttonClassName} style={style}>
      {content}
    </GatsbyLink>
  );
};

export default LinkButton;
